<template>
  <div v-if="!loading" id="edit-vertrag-view">
    <h1 v-if="vertrag != null">{{ vertrag.titel }} bearbeiten</h1>
    <h1 v-else>Neuer Vertrag</h1>

    <div v-if="!loading && vertragErrorMsg != ''" class="alert alert-danger mt-3">Es ist Fehler aufgetreten:<br />{{ vertragErrorMsg }}</div>

    <!-- SELECT VERTRAGSPARTEIEN -->
    <section v-if="displaySection == 0" class="mt-4">
      <h2>Vertragslaufzeit</h2>
      <SetVertragspartein :editVertrag="editVertrag" :guid="guid" />

      <div class="d-flex justify-content-center mt-5">
        <button class="btn btn-secondary" @click="nextSection()">Weiter</button>
      </div>
    </section>

    <!-- SELECT VORLAGE -->
    <section v-if="displaySection == 1" class="mt-4">
      <h2>Bitte wählen Sie eine Vorlage aus</h2>

      <SetVorlage :editVertrag="editVertrag" @vorlage-selected="vorlageSelected" />

      <LoadingSpinner v-if="store.getters.status.getVorlageLoading" />

      <div class="d-flex justify-content-center mt-3">
        <button class="btn btn-secondary me-3" @click="prevSection()">Zurück</button>

        <button class="btn btn-secondary" @click="nextSection()" :disabled="selectedVorlage == null">Weiter</button>
      </div>
    </section>

    <!-- SELECT ABSCHNITTE -->
    <section v-else-if="displaySection == 2 && selectedVorlage != null" class="mt-4">
      <h2>Bitte wählen Sie die Vertragsabschnitte aus</h2>

      <SelectAbschnitte :editVertrag="editVertrag" />

      <div class="d-flex justify-content-center mt-3">
        <button class="btn btn-secondary me-3" @click="prevSection()">Zurück</button>
        <button class="btn btn-secondary" @click="generateInputs()" :disabled="editVertrag.textabschnitte.length == 0">Weiter</button>
      </div>

      <!-- <pre>{{ editVertrag.textabschnitte }}</pre> -->
    </section>

    <!-- VARIABLEN INPUT -->
    <section v-else-if="displaySection == 3" class="mt-4">
      <div class="w-50 mx-auto">
        <h2>Bitte füllen Sie die Eingabefelder aus</h2>
      </div>

      <SetInputs :editVertrag="editVertrag" :invalid-variablen="invalidVariablen" />

      <div class="d-flex justify-content-center mt-3">
        <button class="btn btn-secondary me-3" @click="prevSection()">Zurück</button>
        <!-- <button class="btn btn-primary" @click="saveVertrag()">Speichern</button> -->
      </div>
    </section>

    <div v-if="!loading && vertragErrorMsg != ''" class="alert alert-danger mt-3">Es ist Fehler aufgetreten:<br />{{ vertragErrorMsg }}</div>

    <div class="save-bar bg-white position-fixed bottom-0 end-0 p-3 w-100 shadow border-top d-flex justify-content-end">
      <button class="btn btn-secondary" @click="cancel()">Abbrechen</button>
      <button v-if="editVertrag.id != 0 || displaySection == 3" class="btn btn-primary ms-3" @click="saveVertrag()">Speichern</button>
    </div>

    <!-- <pre>{{ editVertrag }}</pre> -->
  </div>

  <ConfirmationAlert ref="confirmationAlertRef" />

  <LoadingOverlay v-if="loading" :loading="loading" />
</template>

<script lang="ts" setup>
  //#region Imports
  import { computed, ref, PropType, onMounted } from 'vue';
  import { useStore } from '@/store';
  import { useRouter } from 'vue-router';
  import { useDateConverter } from '@/utils/date_converter';

  import { ActionTypes as VertragActionTypes } from '@/store/modules/Vertraege/actions';
  import { ActionTypes as VorlagenActionTypes } from '@/store/modules/Vorlagen/actions';
  import { ActionTypes as VariablenActionTypes } from '@/store/modules/Variablen/actions';
  import { ActionTypes as VertragspartnerActionTypes } from '@/store/modules/Vertragspartner/actions';
  import { ActionTypes as PersonalActionTypes } from '@/store/modules/Personaldaten/actions';

  import { Personaldaten } from '@/models/PersonaldatenModels';
  import { Input, TextAbschnitt, Vertrag } from '@/models/VertragModels';
  import { Vorlage, Variable, Textbaustein } from '@/models/VorlageModels';
  import { Signatur, Vertragspartner } from '@/models/VertragspartnerModels';
  import { GetPersonaldatenRequest } from '@/models/PersonaldatenModels';

  import SetVertragspartein from '@/components/Vertrag/EditVertrag/SetVertragspartein.vue';
  import SetVorlage from '@/components/Vertrag/EditVertrag/SetVorlage.vue';
  import SelectAbschnitte from '@/components/Vertrag/EditVertrag/SelectAbschnitte.vue';
  import SetInputs from '@/components/Vertrag/EditVertrag/SetInputs.vue';
  import ConfirmationAlert from '@/components/ConfirmationAlert.vue';
  import LoadingOverlay from '@/components/LoadingOverlay.vue';
  import LoadingSpinner from '@/components/LoadingSpinner.vue';
  //#endregion

  const props = defineProps({
    vertragid: {
      type: String as PropType<string>,
      default: '',
    },

    guid: {
      type: String as PropType<string>,
      default: '',
    },
  });

  const store = useStore();
  const router = useRouter();

  const { dateToDateInputConverter, dateToDateConverter } = useDateConverter();

  //#region Sections
  const displaySection = ref(0);

  function nextSection() {
    if (editVertrag.value.id != 0 && displaySection.value == 0) {
      displaySection.value = 2;
      checkIfVorlageSectionsUpdated();
    } else {
      displaySection.value++;
    }

    // Scroll to top of the page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function prevSection() {
    if (editVertrag.value.id != 0 && displaySection.value == 2) {
      displaySection.value = 0;
    } else if (displaySection.value > 0) {
      displaySection.value--;
    }

    // Scroll to top of the page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  //#endregion

  //#region Vertrag
  const vertrag = computed(() => store.getters.vertraege.find((v: Vertrag) => v.id == parseInt(props.vertragid)));

  const editVertrag = ref<Vertrag>(vertrag.value != null ? (JSON.parse(JSON.stringify(vertrag.value)) as Vertrag) : (new Vertrag() as Vertrag));
  //#endregion

  //#region Vorlage
  const selectedVorlage = ref<Vorlage>();
  function vorlageSelected(vorlageId: number) {
    selectedVorlage.value = store.getters.vorlagen.find((v: Vorlage) => v.id == vorlageId);
    nextSection();
  }

  function checkIfVorlageSectionsUpdated() {
    let vorlageUpdated = false;
    // alert('checkIfVorlageSectionsUpdated called');
    if (editVertrag.value != undefined && editVertrag.value.vorlageid != undefined && editVertrag.value.vorlageid != 0) {
      const vorlage = store.getters.vorlagen.find((v: Vorlage) => v.id == editVertrag.value.vorlageid);

      if (vorlage != undefined && vorlage.vorlageTextbausteine.length > 0) {
        // REMOVE Abschnitte that are in editVertrag but not in the vorlage anymore
        const removedDeletedAbschnitteList = editVertrag.value.textabschnitte.filter((ea) =>
          vorlage.vorlageTextbausteine.map((t) => t.textbausteinid).includes(ea.textbausteinid)
        );

        if (removedDeletedAbschnitteList.length != editVertrag.value.textabschnitte.length) {
          vorlageUpdated = true;
        }

        editVertrag.value.textabschnitte = removedDeletedAbschnitteList;

        for (const abschnitt of vorlage.vorlageTextbausteine) {
          // Check if the textabschnitt is already in the editVertrag
          const existingTextabschnitt = editVertrag.value.textabschnitte.find((t) => t.textbausteinid == abschnitt.textbausteinid);
          if (existingTextabschnitt == undefined) {
            // If not, add it to the editVertrag
            vorlageUpdated = true;

            // Subabschnitte
            const vertragSubAbschnitt = new Array<TextAbschnitt>();

            abschnitt.textbaustein.abschnitte.forEach((subabschnitt: Textbaustein) => {
              const newSubabschnitt = new TextAbschnitt(
                0,
                editVertrag.value.id,
                subabschnitt.position,
                false,
                // abschnitt.optional,
                subabschnitt.id,
                null,
                new Array<TextAbschnitt>()
              );
              newSubabschnitt.textbaustein = subabschnitt;
              vertragSubAbschnitt.push(newSubabschnitt);
            });

            // Abschnitt
            const vertragAbschnitt = new TextAbschnitt(
              0,
              editVertrag.value.id,
              abschnitt.position,
              !abschnitt.optional,
              // abschnitt.optional,
              abschnitt.textbaustein.id,
              null,
              vertragSubAbschnitt
            );
            vertragAbschnitt.textbaustein = abschnitt.textbaustein;

            if (editVertrag.value.textabschnitte.findIndex((a: TextAbschnitt) => a.textbausteinid == abschnitt.textbaustein.id) == -1) {
              editVertrag.value.textabschnitte.push(vertragAbschnitt);
            }
          } else {
            if (existingTextabschnitt.position != abschnitt.position) {
              vorlageUpdated = true;
            }

            existingTextabschnitt.position = abschnitt.position; // Update position if it exists
            // existingTextabschnitt.optional = abschnitt.optional; // Update optional if it exists

            if (abschnitt.textbaustein.abschnitte.length > 0) {
              // REMOVE Subabschnitte that are in Abschnitt but not in the vorlage anymore
              // Subabschnitte werden automatisch gelöscht, wenn Subabschnitt aus Textbausteine entfernt werden

              // If the textabschnitt has sub-abschnitte, check if they are already in the editVertrag
              for (const subAbschnitt of abschnitt.textbaustein.abschnitte) {
                const existingSubAbschnitt = existingTextabschnitt.abschnitte.find((sa) => sa.textbausteinid == subAbschnitt.id);
                if (existingSubAbschnitt == undefined) {
                  // If not, add it to the existingTextabschnitt
                  vorlageUpdated = true;

                  const newSubAbschnitt = new TextAbschnitt(
                    0,
                    editVertrag.value.id,
                    subAbschnitt.position,
                    false,
                    // abschnitt.optional,
                    subAbschnitt.id,
                    null,
                    new Array<TextAbschnitt>()
                  );
                  newSubAbschnitt.textbaustein = subAbschnitt;

                  existingTextabschnitt.abschnitte.push(newSubAbschnitt);

                  // const newSubAbschnitt = JSON.parse(JSON.stringify(subAbschnitt)) as TextAbschnitt;
                  // newSubAbschnitt.selected = true; // Set selected to true by default
                  // existingTextabschnitt.abschnitte.push(newSubAbschnitt);
                } else {
                  if (subAbschnitt.position != existingSubAbschnitt.position) {
                    vorlageUpdated = true;
                  }

                  existingSubAbschnitt.position = subAbschnitt.position; // Update position if it exists
                }
              }
            }
          }
        }
      }
    }

    if (vorlageUpdated) {
      // Reset inputs if the vorlage was updated
      confirmationAlertRef.value?.showAlert(
        'Die Vorlage wurde überarbeitet',
        'Die Vorlage wurde überarbeitet und die Abschnitte wurden angepasst.',
        '',
        '',
        'warning',
        'text-dark',
        '',
        false
      );
    }
  }
  //#endregion

  //#region Inputs
  const variablen = computed(() => store.getters.variablen);
  const invalidVariablen = ref<Array<string>>([]);

  function generateInputs() {
    invalidVariablen.value = []; // Reset invalid variables for each section
    const newInputs = new Array<Input>();

    const titleInputs = getTextInputs(selectedVorlage.value != undefined ? selectedVorlage.value.titel : editVertrag.value.titel);
    titleInputs.forEach((i) => {
      if (newInputs.findIndex((ni) => ni.variableid == i.variableid) == -1) {
        newInputs.push(i);
      }
    });

    const titleENInputs = getTextInputs(selectedVorlage.value != undefined ? selectedVorlage.value.titelEN : editVertrag.value.titelEN);
    titleENInputs.forEach((i) => {
      if (newInputs.findIndex((ni) => ni.variableid == i.variableid) == -1) {
        newInputs.push(i);
      }
    });

    // Generate new inputs from selected textabschnitte
    editVertrag.value.textabschnitte
      .filter((t) => t.selected)
      .forEach((t) => {
        const abschnittInputs = getTextabschnittInputs(t);
        abschnittInputs.forEach((i) => {
          if (newInputs.findIndex((ni) => ni.variableid == i.variableid) == -1) {
            newInputs.push(i);
          }
        });

        const abschnittTitleInputs = getTextInputs(t.textbaustein.titel);
        abschnittTitleInputs.forEach((i) => {
          if (newInputs.findIndex((ni) => ni.variableid == i.variableid) == -1) {
            newInputs.push(i);
          }
        });

        const abschnittTitleENInputs = getTextInputs(t.textbaustein.titelEN);
        abschnittTitleENInputs.forEach((i) => {
          if (newInputs.findIndex((ni) => ni.variableid == i.variableid) == -1) {
            newInputs.push(i);
          }
        });
      });

    // Remove all inputs that are not in the newInputs
    const inputsToRemove = editVertrag.value.inputs.filter((i) => newInputs.findIndex((ni) => ni.variableid == i.variableid) == -1);
    if (inputsToRemove.length > 0) {
      editVertrag.value.inputs = editVertrag.value.inputs.filter((i) => inputsToRemove.findIndex((ei) => ei.variableid == i.variableid) == -1);
    }

    // Add all new inputs to the editVertrag
    newInputs.forEach((i) => {
      if (editVertrag.value.inputs.findIndex((ei) => ei.variableid == i.variableid) == -1) {
        editVertrag.value.inputs.push(i);
      }
    });

    nextSection();
  }

  const personaldaten = computed<Personaldaten>(() => {
    if (editVertrag.value.personaldatenGuid) {
      const pers = store.getters.personaldaten.find((p: Personaldaten) => p.guid == editVertrag.value.personaldatenGuid);
      return pers != undefined ? pers : new Personaldaten();
    } else {
      return new Personaldaten();
    }
  });

  function getTextabschnittInputs(abschnitt: TextAbschnitt): Array<Input> {
    // const variableRegex = /&lt;&lt;([^&]+)&gt;&gt;/g;
    const inputs = getTextInputs(abschnitt.textbaustein.text);

    // let match;
    // while ((match = variableRegex.exec(abschnitt.textbaustein.text)) !== null) {
    //   const variableText = `<<${match[1]}>>`;
    //   const variable = variablen.value.find((v: Variable) => v.platzhalter == variableText);

    //   if (variable != null && inputs.findIndex((i: Input) => i.variableid == variable.id) == -1) {
    //     const newInput = new Input(variable.id, variable, editVertrag.value.id, '');

    //     if (variable.herkunft == 'Personaldaten' && variable.feld != null && variable.feld != '' && personaldaten.value.hasOwnProperty(variable.feld)) {
    //       if (variable.type == 'date') {
    //         newInput.value = dateToDateInputConverter(personaldaten.value[variable.feld as keyof Personaldaten].toString());
    //       } else {
    //         newInput.value = personaldaten.value[variable.feld as keyof Personaldaten].toString();
    //       }
    //     }

    //     if (variable.herkunft == 'Vertrag' && variable.feld != null && variable.feld != '') {
    //       if (variable.feld.includes('.')) {
    //         const felder = variable.feld.split('.');
    //         if (variable.type == 'date') {
    //           const parentField = editVertrag.value[felder[0] as keyof Vertrag];
    //           if (parentField && typeof parentField === 'object' && felder[1] in parentField) {
    //             newInput.value = dateToDateInputConverter((parentField as any)[felder[1]].toString());
    //           }
    //         } else {
    //           const parentField = editVertrag.value[felder[0] as keyof Vertrag];
    //           if (parentField && typeof parentField === 'object' && felder[1] in parentField) {
    //             newInput.value = (parentField as any)[felder[1]];
    //           }
    //         }
    //       } else if (editVertrag.value.hasOwnProperty(variable.feld)) {
    //         if (variable.type == 'date') {
    //           newInput.value = dateToDateInputConverter(editVertrag.value[variable.feld as keyof Vertrag]?.toString() || '');
    //         } else {
    //           newInput.value = editVertrag.value[variable.feld as keyof Vertrag]?.toString() || '';
    //         }
    //       }
    //     }

    //     inputs.push(newInput);
    //   } else if (variable == null) {
    //     // Variable not found, add to invalidVariablen
    //     if (!invalidVariablen.value.includes(variableText)) {
    //       invalidVariablen.value.push(variableText);
    //     }
    //   }
    // }

    // Get Subabschnitt-Inputs
    if (abschnitt.abschnitte.length > 0) {
      abschnitt.abschnitte
        .filter((t) => t.selected)
        .forEach((sa) => {
          const subAbschnittInputs = getTextabschnittInputs(sa);
          subAbschnittInputs.forEach((i) => {
            if (inputs.findIndex((ei) => ei.variableid == i.variableid) == -1) {
              inputs.push(i);
            }
          });
        });
    }

    return inputs;
  }

  function getTextInputs(text: string): Array<Input> {
    // const variableRegex = /&lt;&lt;([^&]+)&gt;&gt;/g;
    const variableRegex = /(?:&lt;&lt;|<<)([^&<>]+?)(?:&gt;&gt;|>>)/g;
    const inputs = new Array<Input>();

    let match;
    while ((match = variableRegex.exec(text)) !== null) {
      const variableText = `<<${match[1]}>>`;
      const variable = variablen.value.find((v: Variable) => v.platzhalter == variableText);

      if (variable != null && inputs.findIndex((i: Input) => i.variableid == variable.id) == -1) {
        const newInput = new Input(variable.id, variable, editVertrag.value.id, '');

        if (variable.herkunft == 'Personaldaten' && variable.feld != null && variable.feld != '' && personaldaten.value.hasOwnProperty(variable.feld)) {
          if (variable.type == 'date') {
            newInput.value = dateToDateInputConverter(personaldaten.value[variable.feld as keyof Personaldaten].toString());
          } else {
            newInput.value = personaldaten.value[variable.feld as keyof Personaldaten].toString();
          }
        }

        if (variable.herkunft == 'Vertrag' && variable.feld != null && variable.feld != '') {
          if (variable.feld.includes('.')) {
            const felder = variable.feld.split('.');
            if (variable.type == 'date') {
              const parentField = editVertrag.value[felder[0] as keyof Vertrag];
              if (parentField && typeof parentField === 'object' && felder[1] in parentField) {
                newInput.value = dateToDateInputConverter((parentField as any)[felder[1]].toString());
              }
            } else {
              const parentField = editVertrag.value[felder[0] as keyof Vertrag];
              if (parentField && typeof parentField === 'object' && felder[1] in parentField) {
                newInput.value = (parentField as any)[felder[1]];
              }
            }
          } else if (editVertrag.value.hasOwnProperty(variable.feld)) {
            if (variable.type == 'date') {
              newInput.value = dateToDateInputConverter(editVertrag.value[variable.feld as keyof Vertrag]?.toString() || '');
            } else {
              newInput.value = editVertrag.value[variable.feld as keyof Vertrag]?.toString() || '';
            }
          }
        }

        inputs.push(newInput);
      } else if (variable == null) {
        // Variable not found, add to invalidVariablen
        if (!invalidVariablen.value.includes(variableText)) {
          invalidVariablen.value.push(variableText);
        }
      }
    }

    return inputs;
  }
  //#endregion

  //#region SUMMEN-Variablen Inputs
  function calcSummenInputs() {
    // get Inputs where Variable type is 'summe'
    const summeInputs = editVertrag.value.inputs.filter((i) => i.variable.values.length > 0 && i.variable.values[0].type == 'summe');
    if (summeInputs.length == 0) {
      return;
    }

    // Für jeden Input vom Typ Summe den Wert berechnen
    for (const sInput of summeInputs) {
      let summe = 0;
      // In den Values stehen die IDs der Variablen aus denen die Summe gebildet werden soll.
      // Über die VariablenID kann auf den entsprechenden Input zugegriffen werden und dessen Wert (value) verwendet werden.
      for (const val of sInput.variable.values) {
        const varInt = parseInt(val.value);
        if (!isNaN(varInt)) {
          const sInput = editVertrag.value.inputs.find((i) => i.variableid == varInt);
          if (sInput != undefined) {
            summe += parseFormatedNumber(sInput.value);
          }
        }
      }

      if (summe > 0) {
        sInput.value = formatCurrency(summe);
      }
    }
  }

  // Funktion zum Parsen von formatierten Zahlen (z.B. "1.234,56") als String zu Number
  function parseFormatedNumber(str: string): number {
    if (!str) return 0;
    // Entfernt Punkte (Tausendertrennzeichen) und ersetzt Komma durch Punkt
    let normalized = str.replace(/\./g, '').replace(',', '.');
    return parseFloat(normalized);
  }

  /**
   * Formatiert eine Zahl als Währung mit 2 Dezimalstellen, Tausenderpunkten und Komma
   * Beispiel: 1234.5 → "1.234,50"
   */
  function formatCurrency(value: number): string {
    return value.toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  //#endregion

  //#region Speichern
  const loading = computed(
    () => store.getters.status.getVertraegeLoading || store.getters.status.createVertragLoading || store.getters.status.updateVertragLoading
  );
  const vertragErrorMsg = computed(() => store.getters.status.vertragErrorMsg);

  // Variablen Inputs mit Herkunft Vertrag aktualisieren
  function updateVertragInputs() {
    // console.log(editVertrag.value.inputs);
    editVertrag.value.inputs
      .filter((i) => i.variable.herkunft.toLowerCase() == 'vertrag')
      .forEach((i) => {
        if (i.variable.feld.includes('.')) {
          const felder = i.variable.feld.split('.');
          if (i.variable.type == 'date') {
            const parentField = editVertrag.value[felder[0] as keyof Vertrag];
            if (parentField && typeof parentField === 'object' && felder[1] in parentField) {
              i.value = dateToDateInputConverter((parentField as any)[felder[1]].toString());
            }
          } else {
            const parentField = editVertrag.value[felder[0] as keyof Vertrag];
            if (parentField && typeof parentField === 'object' && felder[1] in parentField) {
              i.value = (parentField as any)[felder[1]];
            }
          }
        } else if (editVertrag.value.hasOwnProperty(i.variable.feld)) {
          if (i.variable.type == 'date') {
            i.value = dateToDateInputConverter(editVertrag.value[i.variable.feld as keyof Vertrag]?.toString() || '');
          } else {
            i.value = editVertrag.value[i.variable.feld as keyof Vertrag]?.toString() || '';
            i.valueEN = editVertrag.value[`${i.variable.feld}EN` as keyof Vertrag]?.toString() || '';
          }
        }
      });
  }

  async function checkSignaturen() {
    if (editVertrag.value.zeichnung1.trim() != '' && editVertrag.value.unterschrift1.trim() != '' && editVertrag.value.funktion1.trim() != '') {
      const signatur1Exists = store.getters.signaturen.find(
        (s: Signatur) => s.unterschrift.trim().toLowerCase() == editVertrag.value.unterschrift1.trim().toLowerCase()
      );

      if (signatur1Exists) {
        if (
          signatur1Exists.zeichnung != editVertrag.value.zeichnung1 ||
          signatur1Exists.unterschrift != editVertrag.value.unterschrift1 ||
          signatur1Exists.funktion != editVertrag.value.funktion1 ||
          signatur1Exists.funktionEN != editVertrag.value.funktion1EN
        ) {
          signatur1Exists.zeichnung = editVertrag.value.zeichnung1;
          signatur1Exists.unterschrift = editVertrag.value.unterschrift1;
          signatur1Exists.funktion = editVertrag.value.funktion1;
          signatur1Exists.funktionEN = editVertrag.value.funktion1EN;

          await store.dispatch(VertragspartnerActionTypes.UpdateSignatur, signatur1Exists);
        }
      } else {
        const newSignatur1 = {
          id: 0,
          zeichnung: editVertrag.value.zeichnung1,
          unterschrift: editVertrag.value.unterschrift1,
          funktion: editVertrag.value.funktion1,
          funktionEN: editVertrag.value.funktion1EN,
        } as Signatur;

        await store.dispatch(VertragspartnerActionTypes.CreateSignatur, newSignatur1);
      }
    }

    if (editVertrag.value.zeichnung2.trim() != '' && editVertrag.value.unterschrift2.trim() != '' && editVertrag.value.funktion2.trim() != '') {
      const signatur2Exists = store.getters.signaturen.find(
        (s: Signatur) =>
          s.unterschrift.trim().toLowerCase() == editVertrag.value.unterschrift2.trim().toLowerCase() &&
          s.funktion.trim().toLowerCase() == editVertrag.value.funktion2.trim().toLowerCase()
      );

      if (signatur2Exists) {
        if (
          signatur2Exists.zeichnung != editVertrag.value.zeichnung2 ||
          signatur2Exists.unterschrift != editVertrag.value.unterschrift2 ||
          signatur2Exists.funktion != editVertrag.value.funktion2 ||
          signatur2Exists.funktionEN != editVertrag.value.funktion2EN
        ) {
          signatur2Exists.zeichnung = editVertrag.value.zeichnung2;
          signatur2Exists.unterschrift = editVertrag.value.unterschrift2;
          signatur2Exists.funktion = editVertrag.value.funktion2;
          signatur2Exists.funktionEN = editVertrag.value.funktion2EN;

          await store.dispatch(VertragspartnerActionTypes.UpdateSignatur, signatur2Exists);
        }
      } else {
        const newSignatur2 = {
          id: 0,
          zeichnung: editVertrag.value.zeichnung2,
          unterschrift: editVertrag.value.unterschrift2,
          funktion: editVertrag.value.funktion2,
          funktionEN: editVertrag.value.funktion2EN,
        } as Signatur;

        await store.dispatch(VertragspartnerActionTypes.CreateSignatur, newSignatur2);
      }
    }
  }

  function checkVertragTitle() {
    if (editVertrag.value.vorlageid != null && editVertrag.value.vorlageid != 0) {
      const vorlage = JSON.parse(JSON.stringify(store.getters.vorlagen.find((v: Vorlage) => v.id == editVertrag.value.vorlageid)));
      if (vorlage != null) {
        editVertrag.value.titel = replaceVariablesInText(vorlage.titel, 'de');

        editVertrag.value.titelEN = replaceVariablesInText(vorlage.titelEN, 'en');
      }
    }
  }

  function replaceVariablesInText(text: string, language: string): string {
    for (let input of editVertrag.value.inputs) {
      if (input.variable != null && input.variable.validationType == 'usersignature' && store.getters.authUser?.canSign) {
        const signatureName = store.getters.authUser?.name != '' ? store.getters.authUser?.name : store.getters.authUser?.benutzername || '';
        input.value = signatureName;
      }

      if (input.variable != null && input.variable.type == 'date' && input.variable.validationType == 'today') {
        input.value = new Date().toISOString();
      }

      if (input.value != null && input.value != '') {
        let textKey = input.variable.platzhalter.replace('<<', '&lt;&lt;');
        textKey = textKey.replace('>>', '&gt;&gt;');

        if (input.variable.type.toLowerCase() == 'date') {
          text = text.replaceAll(textKey, dateToDateConverter(input.value));
        } else {
          let inputValue = input.value;

          if (language.toLowerCase() == 'en' && input.valueEN != null && input.valueEN != '') {
            inputValue = input.valueEN;
          }

          text = text.replaceAll(textKey, inputValue);
          text = text.replaceAll(input.variable.platzhalter, inputValue);
        }
      }
    }

    return text;
  }

  function saveVertrag() {
    calcSummenInputs();

    // Variablen Inputs mit Herkunft Vertrag aktualisieren
    updateVertragInputs();
    checkVertragTitle();

    if (vertrag.value != null) {
      store.dispatch(VertragActionTypes.UpdateVertrag, editVertrag.value).then(async () => {
        if (vertragErrorMsg.value == '') {
          await store.dispatch(VertragActionTypes.GetVertragById, editVertrag.value.id);
          await checkSignaturen();
          router.push({ name: 'DetailsVertrag', query: { vertragid: props.vertragid, guid: props.guid } });
        }
      });
    } else {
      store.dispatch(VertragActionTypes.CreateVertrag, editVertrag.value).then(async (id: number | undefined) => {
        if (vertragErrorMsg.value == '') {
          if (id != undefined) {
            await store.dispatch(VertragActionTypes.GetVertragById, id);
            await checkSignaturen();
            router.push({ name: 'DetailsVertrag', query: { vertragid: id, guid: props.guid } });
          } else {
            router.push({ name: 'Vertraege' });
          }
        }
      });
    }
  }
  //#endregion

  function cancel() {
    router.back();
  }

  function setEditVertragData() {
    if (editVertrag.value.vertragspartei1 == null) {
      editVertrag.value.vertragspartei1 = new Vertragspartner();
    }
    if (editVertrag.value.vertragspartei2 == null) {
      editVertrag.value.vertragspartei2 = new Vertragspartner();
    }

    selectedVorlage.value = store.getters.vorlagen.find((v: Vorlage) => v.id == editVertrag.value.vorlageid);
  }

  if (vertrag.value != null && vertrag.value.id != 0) {
    setEditVertragData();
  }

  //#region Confirmation Alert
  const confirmationAlertRef = ref();
  //#endregion

  onMounted(async () => {
    if (props.vertragid != '' && props.vertragid != '0') {
      await store.dispatch(VertragActionTypes.GetVertragById, parseInt(props.vertragid));
    }

    store.dispatch(VorlagenActionTypes.GetVorlagen, undefined);
    store.dispatch(VorlagenActionTypes.GetTextbausteine, undefined);
    store.dispatch(VariablenActionTypes.GetVariablen, undefined);
    store.dispatch(VertragspartnerActionTypes.GetVertragspartner, undefined);
    store.dispatch(PersonalActionTypes.GetMandanten, undefined).then(() => {
      if (store.getters.personaldaten.length == 0) {
        const selectedMandant = sessionStorage.getItem('selectedMandant') != undefined ? JSON.parse(sessionStorage.selectedMandant) : '';
        store.dispatch(PersonalActionTypes.GetPersonaldaten, new GetPersonaldatenRequest(selectedMandant ?? '', null));
      }
    });
    store.dispatch(VertragspartnerActionTypes.GetSignaturen, undefined);
  });
</script>

<style scoped>
  .save-bar {
    z-index: 1;
  }

  #edit-vertrag-view {
    margin-bottom: 70px;
  }
</style>
